import TabPanel from '@/components/ui/tab/TabPanel'
import Tabs from '@/components/ui/tabs/Tabs'
import PC300Mesures from './PC300Mesures'
import DiagnosticMedicalVideoContainer from './DiagnosticMedicalVideo'
import DiagnosticStethoscopeContainer from './DiagnosticStethoscope'
import BiologicalsContainer from './Biologicals'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { DiagnosticRTCProvider } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAttendeeInfo, selectMeetingInfo, selectTwilioStunServer } from '@/redux/reducers/consultation.reducer'
import { selectPeerId, selectRoomByConsultationId } from '@/redux/reducers/room.reducer'
import { getRoomCreatorId } from '@/core/models/room.model'
import { selectCustomDevices } from '@/redux/reducers/custom-device.reducer'
import { Box, Container } from '@mui/system'
import { Alert, Grid } from '@mui/material'
import { SchillerECG } from './SchillerECG'
import { useEffect, useMemo, useState } from 'react'
import { EdanECG } from './EdanECG'
import { TouchECG } from './TouchECG'
import { MIRSpirometry } from './MIRSpirometry'
import { getConfig } from '@/core/classes/MedconnectDevicesAPI'
import { DEVICES } from '@/constants/medconnect-devices-types'
import { getPatientBiometrics } from '@/utils/medconnectDevices/patientBiometrics'
import { selectConsultationById } from '@/redux/reducers/consultation.reducer'

type AllDiagnosticsProps = {
  consultationId?: string
  /** For group consultations: the consultation ID linked to the shared room */
  roomConsultationId?: string
  isStreamer: boolean
  /** If set, switch to this tab by id (e.g. 'stethoscope', 'medicalVideo', 'biologicals', 'mesures') */
  activeTabId?: string | null
  /** Called when the user manually switches tabs, so the parent can clear the controlled activeTabId */
  onTabChange?: (tabId: string) => void
}

const TAB_IDS = ['mesures', 'medicalVideo', 'stethoscope', 'biologicals'] as const

export function AllDiagnostics({ consultationId, roomConsultationId, isStreamer, activeTabId, onTabChange }: Readonly<AllDiagnosticsProps>) {
  const activeTabIndex = activeTabId ? TAB_IDS.indexOf(activeTabId as any) : undefined
  const controlledTab = activeTabIndex !== undefined && activeTabIndex >= 0 ? activeTabIndex : undefined
  const effectiveRoomConsultationId = roomConsultationId || consultationId
  const { t } = useTranslation()
  const peerId = useAppSelector(selectPeerId)
  const room = useAppSelector((state) => (effectiveRoomConsultationId ? selectRoomByConsultationId(state, effectiveRoomConsultationId) : undefined))
  const twilioStunServer = useAppSelector(selectTwilioStunServer)
  const peerConfig = useMemo(
    () =>
      !twilioStunServer
        ? undefined
        : {
          iceServers: twilioStunServer.iceServers,
        },
    [twilioStunServer]
  )

  if (!room) {
    return null
  }

  const peerjsConfig = {
    config: peerConfig,
    peerId,
    streamer: room.streamer,
  }

  return (
    <Container maxWidth="md" sx={{ pt: '20px', pb: '20px' }}>
      <DeviceExamsSection consultationId={consultationId} />
      <Tabs
        value={controlledTab}
        onChange={(newIndex) => { onTabChange?.(TAB_IDS[newIndex]) }}
        renderTabPanel={({ value }) => (
          <>
            <TabPanel value={value} index={0}>
              <PC300Mesures consultationId={consultationId} />
            </TabPanel>
            <TabPanel value={value} index={1} unmountOnExit>
              <MedicalVideoPanel consultationId={consultationId} roomConsultationId={effectiveRoomConsultationId} isStreamer={isStreamer} />
            </TabPanel>
            <TabPanel value={value} index={2} unmountOnExit>
              <StethoscopePanel consultationId={consultationId} roomConsultationId={effectiveRoomConsultationId} isStreamer={isStreamer} />
            </TabPanel>
            <TabPanel value={value} index={3}>
              <BiologicalsContainer consultationId={consultationId} />
            </TabPanel>
          </>
        )}
        tabs={_.compact([
          { label: t('page.localConsultation.sidebar.mesures.label'), sx: { fontWeight: 700 }, id: 'mesures' },
          { label: t('page.liveConsultation.sidebar.medicalVideo.label'), sx: { fontWeight: 700 }, id: 'medicalVideo' },
          { label: t('page.localConsultation.sidebar.stethoscope.label'), sx: { fontWeight: 700 }, id: 'stethoscope' },
          { label: t('page.localConsultation.sidebar.biologicals.label'), sx: { fontWeight: 700 }, id: 'biologicals' },
        ])}
        variant="fullWidth"
      />
    </Container>
  )
}

type DevicePanelProps = {
  consultationId?: string
  /** CSS height of the medical camera video (75vh in the usual consultation) */
  videoHeight?: string
  /** Size the camera frame to the camera's own aspect ratio, capped at `maxVideoHeight` */
  fitVideoToStream?: boolean
  maxVideoHeight?: string
  /** For group consultations: the consultation ID linked to the shared room */
  roomConsultationId?: string
  isStreamer: boolean
}

/** Medical camera stream of the consultation (also used by the simplified reception's doctor step). */
export function MedicalVideoPanel({ consultationId, roomConsultationId, isStreamer, videoHeight = '75vh', fitVideoToStream, maxVideoHeight }: Readonly<DevicePanelProps>) {
  const attendeeInfo = useAppSelector((state) => selectAttendeeInfo(state, 'medicalVideo'))
  const meetingInfo = useAppSelector((state) => selectMeetingInfo(state, 'medicalVideo'))
  const customDevices = useAppSelector(selectCustomDevices)

  return (
    <Box sx={{ height: '100%', display: 'flex', '& > div': { flex: 1 } }}>
      <DiagnosticRTCProvider
        config={{
          chime: {
            attendeeInfo,
            meetingInfo,
          },
          // peerjs: peerjsConfig,
        }}
        customDevices={customDevices}
        isStreamer={isStreamer}
        type="video"
      >
        <DiagnosticMedicalVideoContainer
          videoHeight={videoHeight}
          videoWidth="100vw"
          fitVideoToStream={fitVideoToStream}
          maxVideoHeight={maxVideoHeight}
          consultationId={consultationId}
          roomConsultationId={roomConsultationId || consultationId}
        />
      </DiagnosticRTCProvider>
    </Box>
  )
}

/** Stethoscope stream of the consultation (also used by the simplified reception's doctor step). */
export function StethoscopePanel({ consultationId, roomConsultationId, isStreamer }: Readonly<DevicePanelProps>) {
  const effectiveRoomConsultationId = roomConsultationId || consultationId
  const attendeeInfo = useAppSelector((state) => selectAttendeeInfo(state, 'stethoscope'))
  const meetingInfo = useAppSelector((state) => selectMeetingInfo(state, 'stethoscope'))
  const customDevices = useAppSelector(selectCustomDevices)
  const room = useAppSelector((state) => (effectiveRoomConsultationId ? selectRoomByConsultationId(state, effectiveRoomConsultationId) : undefined))

  if (!room) return null

  return (
    <DiagnosticRTCProvider
      config={{
        chime: {
          attendeeInfo,
          meetingInfo,
          ecgSourceUserId: getRoomCreatorId(room),
        },
        //peerjs: peerjsConfig,
      }}
      customDevices={customDevices}
      isStreamer={isStreamer}
      type="audio"
    >
      <DiagnosticStethoscopeContainer consultationId={consultationId} roomConsultationId={effectiveRoomConsultationId} />
    </DiagnosticRTCProvider>
  )
}

/**
 * The device exams of the usual consultation (patient height/weight they use + ECG and spirometry device cards,
 * each card shown only when its device is configured on this workstation). Also the simplified reception's ECG tab,
 * which passes its own `biometricsHint` because its vital signs step has another name.
 */
export function DeviceExamsSection({ consultationId, biometricsHint }: Readonly<{ consultationId?: string; biometricsHint?: string }>) {
  const { t } = useTranslation()
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const biometrics = useMemo(() => getPatientBiometrics(consultation), [consultation])

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('component.ui.deviceCard.biometrics.info', {
          height: biometrics.height,
          weight: biometrics.weight,
          heightSource: t(`component.ui.deviceCard.biometrics.${biometrics.heightSource}`),
          weightSource: t(`component.ui.deviceCard.biometrics.${biometrics.weightSource}`),
        })}
        {(biometrics.heightSource === 'default' || biometrics.weightSource === 'default') && (
          <> {biometricsHint ?? t('component.ui.deviceCard.biometrics.hint')}</>
        )}
      </Alert>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <SchillerECG consultationId={consultationId} />
        <EdanECG consultationId={consultationId} />
        <TouchECG consultationId={consultationId} />
        <MIRSpirometry consultationId={consultationId} />
      </Grid>
    </>
  )
}

/** Devices behind the cards of {@link DeviceExamsSection}. */
export const EXAM_DEVICE_NAMES: readonly string[] = [DEVICES.SCHILLER_ECG, DEVICES.SE_1515, DEVICES.TOUCH_ECG, DEVICES.MIR_SPIROMETRY]

/**
 * Whether this workstation's devices connector has at least one ECG / spirometry device configured
 * (false when no connector answers). Same check as the cards themselves.
 */
export function useHasConfiguredExamDevice(): boolean {
  const [hasDevice, setHasDevice] = useState(false)

  useEffect(() => {
    let active = true
    getConfig()
      .then((config) => {
        if (active) setHasDevice(!!config?.Devices?.some((device) => EXAM_DEVICE_NAMES.includes(device.name)))
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [])

  return hasDevice
}
